import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { EMAIL_SALES } from '@app/config';
import { DialogService } from '@app/core';
import { SharedModule } from '@app/shared';
import { environment } from '@env/environment';
import { TuiAppearance, TuiButton, TuiIcon } from '@taiga-ui/core';
import { TuiBadge, TuiBadgedContent, TuiSwitch } from '@taiga-ui/kit';
import { PricingContactSalesDialogComponent } from '../pricing-contact-sales-dialog/pricing-contact-sales-dialog.component';
import { PricingRequestCustomPlanDialogComponent } from '../pricing-request-custom-plan-dialog/pricing-request-custom-plan-dialog.component';

@Component({
  selector: 'app-pricing-plans-section',
  imports: [
    SharedModule,
    TuiAppearance,
    TuiBadge,
    TuiBadgedContent,
    TuiButton,
    TuiIcon,
    TuiSwitch,
  ],
  templateUrl: './pricing-plans-section.component.html',
  styleUrl: './pricing-plans-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PricingPlansSectionComponent {
  private readonly dialogService = inject(DialogService);
  protected readonly consoleBaseUrl = environment.consoleBaseUrl;
  protected readonly emailSales = EMAIL_SALES;
  protected readonly formGroup = new FormGroup({
    isYearlyCycle: new FormControl(false),
  });
  protected readonly plans = [
    {
      label: 'Starter',
      description: 'Perfect for individual developers getting started.',
      highlighted: false,
      monthlyPriceInCents: 900,
      yearlyPriceInCents: 9000,
      features: [
        '1,000 Live Updates',
        '200 Build Minutes (~40 builds)',
        '1 Concurrent Build',
        'Large Build Machines (macOS M4)',
        'App Store Publishing',
        '30 Days Data Retention',
      ],
    },
    {
      label: 'Professional',
      description: 'Best for small teams and growing apps.',
      highlighted: false,
      monthlyPriceInCents: 2900,
      yearlyPriceInCents: 29_000,
      features: [
        '10,000 Live Updates',
        '600 Build Minutes (~120 builds)',
        '2 Concurrent Build',
        'Large Build Machines (macOS M4)',
        'App Store Publishing',
        '30 Days Data Retention',
      ],
    },
    {
      label: 'Team',
      description: 'Ideal for teams with active development cycles.',
      highlighted: true,
      monthlyPriceInCents: 9900,
      yearlyPriceInCents: 99_000,
      features: [
        '100,000 Live Updates',
        '1,800 Build Minutes (~360 builds)',
        '3 Concurrent Builds',
        'Large Build Machines (macOS M4)',
        'App Store Publishing',
        '60 Days Data Retention',
      ],
    },
    {
      label: 'Business',
      description: 'Perfect for organizations managing apps at scale.',
      highlighted: false,
      monthlyPriceInCents: 29_900,
      yearlyPriceInCents: 299_000,
      features: [
        '1,000,000 Live Updates',
        '5,400 Build Minutes (~1,080 builds)',
        '4 Concurrent Builds',
        'Large Build Machines (macOS M4)',
        'App Store Publishing',
        '90 Days Data Retention',
      ],
    },
  ];

  protected async handleRequestCustomPlan(): Promise<void> {
    await this.dialogService.showComponentDialog(
      PricingRequestCustomPlanDialogComponent,
    );
  }

  protected async handleContactSales(): Promise<void> {
    await this.dialogService.showComponentDialog(
      PricingContactSalesDialogComponent,
    );
  }
}
